import { useState, useCallback, useRef } from 'react';
import { suggestSearchTermsAI } from '../firebase/ai';

/**
 * Hook for AI-powered search term suggestions
 * Handles loading states, errors, and debouncing
 */
export const useSearchTermSuggestions = () => {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  const generateSuggestions = useCallback(async (organizationName) => {
    // Validate input
    if (!organizationName?.trim() || organizationName.trim().length < 2) {
      return null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const result = await suggestSearchTermsAI(organizationName.trim());

      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        return null;
      }

      const data = result.data || result;
      setSuggestions(data);
      return data;
    } catch (err) {
      // Don't set error if request was aborted
      if (err.name === 'AbortError') {
        return null;
      }
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearSuggestions = useCallback(() => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setSuggestions(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    suggestions,
    loading,
    error,
    generateSuggestions,
    clearSuggestions
  };
};

export default useSearchTermSuggestions;
